import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calculator as CalcIcon, 
  LogOut, 
  Trash2, 
  ArrowLeft,
  FolderOpen,
  Upload
} from "lucide-react";
import { Button } from "../components/ui/button";
import AdBanner from "../components/AdBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUser();
    fetchProjects();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      navigate('/');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        withCredentials: true
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Delete this template?')) return;

    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        withCredentials: true
      });
      setProjects(projects.filter(p => p.project_id !== projectId));
      toast.success('Template deleted');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleLoadTemplate = (template) => {
    const settingsToLoad = {
      grossPrice: template.gross_price || '',
      isBumi: template.is_bumi,
      bumiPercentage: template.bumi_percentage || 7,
      formulaString: template.formula_string,
      showAdvanced: template.show_advanced || false,
      dpPercentage: template.dp_percentage || 10
    };
    
    // Save to localStorage so Calculator can load it
    localStorage.setItem('load_template', JSON.stringify(settingsToLoad));
    toast.success(`Template "${template.name}" loaded!`);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#09090b] pb-[70px]">
      <div className="max-w-md mx-auto p-5 space-y-6">
        <div className="relative pt-8">
          <div 
            className="absolute inset-0 -top-20 opacity-30 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 50% 0%, rgba(16, 185, 129, 0.15) 0%, transparent 50%)'
            }}
          />

          <div className="relative space-y-6">
            <div className="flex items-center justify-between">
              <Button
                data-testid="back-to-calculator-btn"
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Calculator
              </Button>
              
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-zinc-100">My Templates</h1>
                <p className="text-sm text-zinc-500">Welcome back, {user?.name?.split(' ')[0]}</p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <p className="text-zinc-500">Loading projects...</p>
              </div>
            ) : projects.length === 0 ? (
              <div 
                data-testid="empty-state"
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-8 text-center space-y-4"
              >
                <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center mx-auto">
                  <CalcIcon className="w-8 h-8 text-zinc-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-zinc-300 mb-1">No Templates Yet</h3>
                  <p className="text-sm text-zinc-500">
                    Create your first rebate template to reuse settings
                  </p>
                </div>
                <Button
                  data-testid="goto-calculator-btn"
                  onClick={() => navigate('/')}
                  className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold"
                >
                  <CalcIcon className="w-4 h-4 mr-2" />
                  Start Calculating
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="projects-list">
                {projects.map((project) => (
                  <div
                    key={project.project_id}
                    data-testid={`project-card-${project.project_id}`}
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3 hover:border-zinc-700 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-zinc-100 mb-1">{project.name}</h3>
                        <p className="text-xs text-zinc-500">
                          {new Date(project.created_at).toLocaleDateString('en-MY', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      <Button
                        data-testid={`delete-project-${project.project_id}`}
                        onClick={() => handleDelete(project.project_id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <div className="bg-zinc-800/50 rounded-lg p-3">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Formula</p>
                        <p className="font-mono text-emerald-400 font-semibold">{project.formula_string}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        {project.is_bumi && (
                          <div className="bg-zinc-800/50 rounded-lg p-2">
                            <p className="text-xs text-zinc-500 uppercase tracking-wider">Bumi</p>
                            <p className="text-sm font-semibold text-zinc-200">{project.bumi_percentage || 7}%</p>
                          </div>
                        )}
                        {project.show_advanced && (
                          <div className="bg-zinc-800/50 rounded-lg p-2">
                            <p className="text-xs text-zinc-500 uppercase tracking-wider">Down Payment</p>
                            <p className="text-sm font-semibold text-zinc-200">{project.dp_percentage || 10}%</p>
                          </div>
                        )}
                        {project.gross_price && (
                          <div className="bg-zinc-800/50 rounded-lg p-2">
                            <p className="text-xs text-zinc-500 uppercase tracking-wider">Reference Price</p>
                            <p className="text-sm font-semibold text-zinc-200 font-mono">
                              RM {project.gross_price.toLocaleString('en-MY', { maximumFractionDigits: 0 })}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <Button
                      data-testid={`load-template-${project.project_id}`}
                      onClick={() => handleLoadTemplate(project)}
                      className="w-full rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 font-semibold border border-emerald-500/30 transition-all"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Load Template
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AdBanner />
    </div>
  );
};

export default Dashboard;